import React, { useState, useEffect } from 'react';

const GitHubActionsSidebar = () => {
  const [workflows, setWorkflows] = useState([]);
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Mock workflow data - in production, this would fetch from GitHub API
  useEffect(() => {
    const mockWorkflows = [
      {
        id: 1,
        name: 'Content Discovery',
        description: 'Scans for missing syllabi and vocabulary files',
        status: 'success',
        lastRun: '2 hours ago',
        nextRun: 'Daily at 2 AM UTC',
        icon: '🔍',
        file: 'discover-content-gaps.yaml'
      },
      {
        id: 2,
        name: 'Syllabus Generation',
        description: 'Creates comprehensive course syllabi using AI',
        status: 'running',
        lastRun: 'Running now',
        nextRun: 'On demand / Daily check',
        icon: '📚',
        file: 'generate-syllabus.yaml'
      },
      {
        id: 3,
        name: 'Vocabulary Generation',
        description: 'Generates weekly vocabulary lists for courses',
        status: 'success',
        lastRun: '1 day ago',
        nextRun: 'Weekly on Monday',
        icon: '📝',
        file: 'generate-vocabulary.yaml'
      },
      {
        id: 4,
        name: 'Content Validation',
        description: 'AI-powered quality assurance for educational content',
        status: 'success',
        lastRun: '4 hours ago',
        nextRun: 'Daily at 4 AM UTC',
        icon: '✅',
        file: 'validate-content.yaml'
      },
      {
        id: 5,
        name: 'Course Updates',
        description: 'Updates core.js with new courses and metadata',
        status: 'pending',
        lastRun: '3 days ago',
        nextRun: 'On content changes',
        icon: '🔄',
        file: 'update-course-list.yaml'
      },
      {
        id: 6,
        name: 'Production Monitor',
        description: 'Monitors platform performance and engagement',
        status: 'success',
        lastRun: '6 hours ago',
        nextRun: 'Every 6 hours',
        icon: '📊',
        file: 'production-monitoring.yaml'
      }
    ];

    setWorkflows(mockWorkflows);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'text-green-600 bg-green-50 border-green-200';
      case 'running': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'failed': return 'text-red-600 bg-red-50 border-red-200';
      case 'pending': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return '✅';
      case 'running': return '🔄';
      case 'failed': return '❌';
      case 'pending': return '⏳';
      default: return '⚪';
    }
  };

  return (
    <div className={`fixed right-0 top-0 h-full bg-white border-l border-gray-200 shadow-lg transition-transform duration-300 z-50 ${
      isCollapsed ? 'transform translate-x-full' : ''
    }`} style={{ width: isCollapsed ? '48px' : '400px' }}>
      
      {/* Toggle Button */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute -left-12 top-4 bg-white border border-gray-200 rounded-l-lg p-2 hover:bg-gray-50 transition-colors"
        title={isCollapsed ? 'Show GitHub Actions' : 'Hide GitHub Actions'}
      >
        {isCollapsed ? '⚡' : '←'}
      </button>

      {!isCollapsed && (
        <div className="p-4 h-full overflow-y-auto">
          {/* Header */}
          <div className="flex items-center gap-2 mb-6 pb-4 border-b border-gray-200">
            <div className="text-2xl">⚡</div>
            <div>
              <h2 className="font-semibold text-gray-900">GitHub Actions</h2>
              <p className="text-xs text-gray-500">Automation Status</p>
            </div>
          </div>

          {/* Workflows */}
          <div className="space-y-3">
            {workflows.map((workflow) => (
              <div
                key={workflow.id}
                className={`border rounded-lg p-3 transition-colors hover:bg-gray-50 ${getStatusColor(workflow.status)}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{workflow.icon}</span>
                    <div>
                      <h3 className="font-medium text-sm text-gray-900">{workflow.name}</h3>
                      <p className="text-xs text-gray-600 mt-1">{workflow.description}</p>
                    </div>
                  </div>
                  <span className="text-lg">{getStatusIcon(workflow.status)}</span>
                </div>

                <div className="text-xs text-gray-500 space-y-1">
                  <div className="flex justify-between">
                    <span>Last run:</span>
                    <span className="font-medium">{workflow.lastRun}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Next run:</span>
                    <span className="font-medium">{workflow.nextRun}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>File:</span>
                    <span className="font-mono text-xs bg-gray-100 px-1 rounded">{workflow.file}</span>
                  </div>
                </div>

              </div>
            ))}
          </div>

          {/* Summary */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="bg-gray-50 rounded-lg p-3">
              <h4 className="font-medium text-sm text-gray-900 mb-2">System Status</h4>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex justify-between">
                  <span>✅ Success:</span>
                  <span className="font-medium">{workflows.filter(w => w.status === 'success').length}</span>
                </div>
                <div className="flex justify-between">
                  <span>🔄 Running:</span>
                  <span className="font-medium">{workflows.filter(w => w.status === 'running').length}</span>
                </div>
                <div className="flex justify-between">
                  <span>⏳ Pending:</span>
                  <span className="font-medium">{workflows.filter(w => w.status === 'pending').length}</span>
                </div>
                <div className="flex justify-between">
                  <span>❌ Failed:</span>
                  <span className="font-medium">{workflows.filter(w => w.status === 'failed').length}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mt-4">
            <h4 className="font-medium text-sm text-gray-900 mb-2">Quick Actions</h4>
            <div className="space-y-1">
              <button className="w-full text-left text-xs bg-blue-50 text-blue-700 px-2 py-1.5 rounded hover:bg-blue-100 transition-colors">
                🔍 Scan for missing content
              </button>
              <button className="w-full text-left text-xs bg-green-50 text-green-700 px-2 py-1.5 rounded hover:bg-green-100 transition-colors">
                📚 Generate missing syllabi
              </button>
              <button className="w-full text-left text-xs bg-purple-50 text-purple-700 px-2 py-1.5 rounded hover:bg-purple-100 transition-colors">
                📝 Create vocabulary lists
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GitHubActionsSidebar;